import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle, Lock, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. No token provided.');
    }
  }, [token]);

  const passwordStrength = (pw) => {
    if (pw.length < 8) return { label: 'Too short', color: 'bg-red-400', width: '20%' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { label: 'Weak', color: 'bg-amber-400', width: '40%' };
    if (score <= 3) return { label: 'Fair', color: 'bg-yellow-400', width: '60%' };
    if (score <= 4) return { label: 'Good', color: 'bg-green-400', width: '80%' };
    return { label: 'Strong', color: 'bg-green-600', width: '100%' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const strength = newPassword ? passwordStrength(newPassword) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0776BD] via-[#055A92] to-[#044A7A] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/mr-electric-logo.jpg" alt="Mr. Electric" className="h-24 w-auto object-contain rounded-lg bg-white p-2 shadow-lg" />
          </div>
          <p className="text-white/70 mt-1">Inventory Control</p>
        </div>

        <Card className="border-0 shadow-xl rounded-xl">
          {success ? (
            <CardContent className="pt-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle2 size={32} className="text-green-600" />
              </div>
              <h2 className="font-heading text-2xl text-[#1F2937] mb-2" data-testid="reset-success-title">Password Reset!</h2>
              <p className="text-[#6B7280] mb-6">
                Your password has been changed successfully. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => { logout(); navigate('/login'); }}
                className="w-full bg-[#0776BD] hover:bg-[#055A92] text-white rounded-lg"
                data-testid="go-to-login-after-reset"
              >
                Go to Sign In
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center gap-2">
                  <Lock size={22} className="text-[#0776BD]" />
                  <CardTitle className="font-heading text-2xl tracking-tight text-[#1F2937]">Reset Password</CardTitle>
                </div>
                <CardDescription className="text-[#6B7280]">
                  Create a new secure password for your account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-4 rounded-lg">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-medium text-[#1F2937]">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 8 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={!token}
                        data-testid="reset-password-input"
                        className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD] pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1F2937]"
                        tabIndex={-1}
                        data-testid="reset-toggle-password"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {strength && (
                      <div className="space-y-1">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full transition-all ${strength.color}`} style={{ width: strength.width }}></div>
                        </div>
                        <p className="text-xs text-[#6B7280]">
                          Password strength: <span className="font-medium">{strength.label}</span>
                        </p>
                      </div>
                    )}
                    <ul className="text-xs text-[#9CA3AF] space-y-0.5 ml-1">
                      <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                        {newPassword.length >= 8 ? '\u2713' : '\u2022'} At least 8 characters
                      </li>
                      <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                        {/[A-Z]/.test(newPassword) ? '\u2713' : '\u2022'} One uppercase letter
                      </li>
                      <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                        {/[0-9]/.test(newPassword) ? '\u2713' : '\u2022'} One number
                      </li>
                      <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                        {/[^A-Za-z0-9]/.test(newPassword) ? '\u2713' : '\u2022'} One special character
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#1F2937]">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={!token}
                        data-testid="reset-confirm-input"
                        className={`h-12 rounded-lg focus:border-[#0776BD] pr-12 ${confirmPassword && confirmPassword !== newPassword ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1F2937] transition-colors"
                        tabIndex={-1}
                        data-testid="reset-toggle-confirm-password"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== newPassword && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#0776BD] hover:bg-[#055A92] text-white font-semibold rounded-lg shadow-sm"
                    disabled={loading || !token}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => { logout(); navigate('/login'); }}
                    className="text-sm text-[#6B7280] hover:text-[#0776BD] hover:underline"
                  >
                    Back to Sign In
                  </button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
